import { useEffect, useRef, useState } from 'react'
import { ConvertPriceToString, datePlus7DaysYYYYMMDDHHmmss, dateToYYYYMMDD } from '../../Helpers/CustomConverter'
import ProductInOrderSummary from '../../Pages/Shop/Components/ProductComponents/ProductInOrderSummary'
import { useNavigate } from 'react-router-dom'
import { useWarehouse } from '../../Providers/WarehouseProvider'
import { useCompanyInfo } from '../../Providers/CompanyInfoProvider'
import { LuCopy } from "react-icons/lu";
import { toast } from 'react-toastify'
import useGetBankTransferData from './Hooks/useGetBankTransferData'
import { useOrder } from '../../Providers/OrderProvider'
import { SendOrderFiles } from '../../Services/FileService'
import { useConfig } from '../../Providers/ConfigProvider'
import { useCustomer } from '../../Providers/CustomerProvider'
import { useParams } from '../../Providers/ParamsProvider'
import useCommonFunctions from '../../Pages/Shop/Hooks/MobileAndDesktopCommon/useCommonFunctions'
import { useCart } from '../../Providers/CartProvider'
import LoadingSpinner from '../Components/LoadingSpinner'
import { usePayment } from '../../Providers/PaymentProvider'
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'
import ScrollUpArrow from '../Components/ScrollUpArrow'
import { useOffers } from '../../Providers/OffersProvider'
import { useDiscount } from '../../Providers/DiscountProvider'
import { useAuth } from '../../Providers/AuthProvider'
import { formatDocumentNumber } from '../../Helpers/DocumNumHelper'
import useCheckLicense from '../../Hooks/useCheckLicense.ts'
import { Licenses } from '../../Data/Licenses.ts'
import { OverlayTrigger, Popover } from "react-bootstrap"

const OrderCompleted = ({ order, basket, isBankTransfer }) => {
    const { checkLicense } = useCheckLicense()
    const { authState } = useAuth()
    const didSendRef = useRef(false)
    const { warehouses } = useWarehouse()
    let selectedWarehouse = warehouses.find(w => w.id === order.whID)
    const { companyInfoData } = useCompanyInfo()
    const navigate = useNavigate()
    const { bankTransferData } = useGetBankTransferData(order.siteID)
    const { orderInfo, setOrderInfo, clearOrderInfo } = useOrder()
    const { api } = useConfig()
    const { handleGetCreditInfo, clearTemporaryAddressAndAuthorizedPerson } = useCustomer()
    const { isMobile } = useParams()
    const { handleCopyToClipboard } = useCommonFunctions()
    const { getBasketTotalValue, handleClearCartItems } = useCart()
    const totalBasketAmount = getBasketTotalValue(basket.basketItems)
    const { paymentCreate } = usePayment()
    const [paymentUrl, setPaymentUrl] = useState(null)
    const [isPaymentUrlLoading, setIsPaymentUrlLoading] = useState(false)
    const { orderItemsFromOffer } = useOffers()
    const totalBasketAmountOffer = getBasketTotalValue(orderItemsFromOffer)
    const { handleClearVoucher } = useDiscount()

    useEffect(() => { //Czyszczenie danych zamówienia po wyjści z okna podsumowania
        return () => {
            clearOrderInfo()
            handleClearCartItems()
            clearTemporaryAddressAndAuthorizedPerson()
            handleClearVoucher()
        }
    }, [])

    useEffect(() => {
        handleGetCreditInfo()
        if (order && orderInfo.attachments.length > 0) { //Wysłanie załączników po złożeniu zamówienia
            handleSendAttachments()
        }

        if (order && order.payments[0].formId == "O") {
            handleCreatePayment()
        }

    }, [order])

    const handleCreatePayment = async () => {
        setIsPaymentUrlLoading(true)
        const res = await paymentCreate({
            orderId: `${order.supplierOrderID}-${order.siteID}`,
            docOrderId: order.supplierOrderID,
            docOrderDate: dateToYYYYMMDD(),
            siteId: order.siteID,
            userId: authState.user.userId,
            custId: authState.custId,
            amount: (orderItemsFromOffer.length > 0 ? totalBasketAmountOffer.totalGross : totalBasketAmount.totalGross),
            customerEmail: order.orderingPerson.email,
            paymentTitle: `Zam. nr ${formatDocumentNumber(order.supplierOrderID, new Date(), order.siteID)}`,
            gatewayId: -1,
            paymentExpDate: datePlus7DaysYYYYMMDDHHmmss()
        }, orderInfo.onlinePaymentSupplier)
        setPaymentUrl(res)
        setIsPaymentUrlLoading(false)
    }

    const handlePaymentButtonClick = () => {
        if (paymentUrl.length > 0) {
            window.location.href = paymentUrl;
        } else {
            handleCreatePayment()
        }
    }

    const handleSendAttachments = async () => {
        if (didSendRef.current) {
            return
        }
        didSendRef.current = true
        const promise = SendOrderFiles(api, orderInfo.attachments, order.supplierOrderID, order.siteID)
        toast.promise(
            promise,
            {
                pending: 'Wysyłanie załączników...',
                success: {
                    render({ data }) {
                        setOrderInfo(prev => ({
                            ...prev,
                            attachments: []
                        }))
                        return `Poprawnie dodano załącznik do zamówienia`
                    }
                },
                error: {
                    render({ data }) {
                        setOrderInfo(prev => ({
                            ...prev,
                            attachments: []
                        }))
                        return `Błąd w trakcie dodawania załącznika do zamówienia`
                    }
                }
            }
        )
    }

    const mergeOrderBasket = (orderItems, basketItems) => {
        if (orderItems.length === 0 || basketItems.length === 0) {
            return []
        }
        return orderItems.map(item => {
            const basketItem = basketItems.find(b => b.product.index === item.supplierArtCode) || {}
            return {
                index: item.supplierArtCode,
                title: basketItem.product.title,
                label: basketItem.product.label,
                baseUnit: item.orderUnit,
                qty: item.orderedQty,
                priceNet: item.netPrice,
                currency: basketItem.product.currency || '',
                priceGross: item.grossPrice,
                taxRate: basketItem.product.taxRate,
                photoFileNames: basketItem.product.photoFileNames || null
            }
        })
    }

    const email = selectedWarehouse ? (selectedWarehouse.email ? selectedWarehouse.email : companyInfoData.email) : companyInfoData.email
    const phone = selectedWarehouse ? (selectedWarehouse.phone ? selectedWarehouse.phone : companyInfoData.phone) : companyInfoData.phone
    const items = orderItemsFromOffer.length > 0 ? orderItemsFromOffer : basket.basketItems
    const mergedList = mergeOrderBasket(order.items, items)
    const summaryAlign = isMobile ? 'justify-content-start' : 'justify-content-end'

    const headerText = checkLicense([Licenses.TgInstalacje]) ? 'Dziękujemy za zakupy w TG Instalacje' : 'Dziękujemy za zakupy'

    return (
        <ErrorBoundary>
            <ScrollUpArrow />
            <div className='container-fluid container-fluid-with-paddings'>
                <div className="row">
                    <div className="col-12 col-xl-6 mt-5">
                        <div className='order-completed'>
                            <div className='row justify-content-center align-items-center'>
                                <b style={{ fontWeight: '50px' }}>{headerText}</b>
                                <span className='mt-3'>{`Numer zamówienia: ${order.supplierOrderID}/${order.siteID}`}</span>
                                <span> Twoje zamówienie zostało poprawnie złożone i trafiło do realizacji.</span>
                                <span>O jego postępach zostaniesz poinformowany mailowo.</span>
                                <div className='row justify-content-center mt-3'>
                                    <b>Jeśli masz pytania napisz na adres:</b>
                                    <span>{`${email} lub telefoniczne ${phone}`}</span>
                                </div>
                                <div className="d-flex flex-column justify-content-center mt-5">
                                    <b>Do zapłaty: {ConvertPriceToString(orderItemsFromOffer.length > 0 ? totalBasketAmountOffer.totalGross : totalBasketAmount.totalGross)}</b>
                                </div>
                                <div className='mt-3 px-3'>
                                    {order.payments[0].formId == "O" ? (
                                        <PaymentButtonSection
                                            paymentUrl={paymentUrl}
                                            handlePaymentButtonClick={handlePaymentButtonClick}
                                            isPaymentUrlLoading={isPaymentUrlLoading}
                                            navigate={navigate}
                                        />
                                    ) : (
                                        <button className='btn-app-secondary w-100' onClick={() => navigate('/')}>Przejdź na stronę główną</button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {isBankTransfer && bankTransferData && <div className="main-container-holder row d-flex flex-column text-center mt-4 ms-5 me-5" style={{ border: 0 }}>
                            <span>Wykonaj przelew na dane:</span>
                            <div className='d-flex justify-content-center align-items-center' onClick={() => handleCopyToClipboard(bankTransferData.companyName)}>
                                <b>{bankTransferData.companyName}</b>
                                <LuCopy className='ms-3 primary-accent-color-text element-pointer' />
                            </div>
                            <span className='mt-3'>{bankTransferData.bankName}</span>
                            <div className='d-flex justify-content-center align-items-center' onClick={() => handleCopyToClipboard(bankTransferData.accountNo)}>
                                <b>{bankTransferData.accountNo}</b>
                                <LuCopy className='ms-3 primary-accent-color-text element-pointer' />
                            </div>
                            <div className='d-flex justify-content-center align-items-center' onClick={() => handleCopyToClipboard(orderItemsFromOffer.length > 0 ? totalBasketAmountOffer.totalGross : totalBasketAmount.totalGross)}>
                                <span>Kwota do zapłaty: <b>{ConvertPriceToString(orderItemsFromOffer.length > 0 ? totalBasketAmountOffer.totalGross : totalBasketAmount.totalGross)}</b></span>
                                <LuCopy className='ms-3 primary-accent-color-text element-pointer' />
                            </div>
                            <div className='d-flex justify-content-center align-items-center' onClick={() => handleCopyToClipboard(`${order.supplierOrderID}/${order.siteID}`)}>
                                <span>Tytułem <b>{`${order.supplierOrderID}/${order.siteID}`}</b></span>
                                <LuCopy className='ms-3 primary-accent-color-text element-pointer' />
                            </div>
                        </div>}
                    </div>
                    <div className="col-12 col-xl-6 mt-3 product-in-order-summary">
                        <div className="d-flex justify-content-between mt-4 mb-4">
                            <b className='ms-3'>Produkty na zamówieniu</b>
                            {!isMobile && <div className="d-flex flex-column me-5">
                                <b>Wartość netto</b>
                                <span>Wartość brutto</span>
                            </div>}
                        </div>
                        {mergedList.length > 0 && mergedList.map((item, key) =>
                            <ProductInOrderSummary
                                key={key}
                                photoFileName={item.photoFileNames ? item.photoFileNames[0] : null}
                                title={item.title}
                                label={item.label}
                                baseUnit={item.baseUnit}
                                qty={item.qty}
                                priceNet={item.priceNet}
                                currency={item.currency}
                                priceGross={item.priceGross}
                                taxRate={item.taxRate}
                            />
                        )}
                        {/* <div className="row mt-4">
                            <div className={`col-11 d-flex ${summaryAlign}`}>
                                <span className='me-2'>Koszt wysyłki</span>
                                <span>{ConvertPriceToString(order.deliveryCost)}</span>
                            </div>
                        </div> */}
                        <div className="row mt-2 mb-5">
                            <div className={`col-11 d-flex ${summaryAlign}`}>
                                <b className='me-2'>Suma brutto</b>
                                <b>{ConvertPriceToString(orderItemsFromOffer.length > 0 ? totalBasketAmountOffer.totalGross : totalBasketAmount.totalGross)}</b>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    )
}

const PaymentButtonSection = ({ paymentUrl, handlePaymentButtonClick, isPaymentUrlLoading, navigate }) => {
    const popover = <Popover id="popover-basic">
        <Popover.Body className="text-center">
            <span className='primary-accent-color-text'>Spróbuj zapłacić za to zamówienie z zakładki "Zamówienia"</span>
        </Popover.Body>
    </Popover>

    const getPaymentButton = () => {
        if (isPaymentUrlLoading) {
            return <button className='btn-app-disabled flex-fill' disabled>
                <div className="spinner-border spinner-border-sm me-2" role="status" />
                Generowanie linku do płatności
            </button>
        }
        else {
            if (paymentUrl != null && paymentUrl.trim() != '') {
                return <button className='btn-app-primary flex-fill' onClick={() => handlePaymentButtonClick()}>Zapłać</button>
            }
            else {
                return <OverlayTrigger trigger={["hover", "focus"]} placement="bottom" overlay={popover}>
                    <button className='btn-app-disabled flex-fill' disabled>Błąd w trakcie generowania linku do płatności</button>
                </OverlayTrigger>
            }
        }
    }

    return (
        <div className='d-flex gap-2'>
            {getPaymentButton()}
            <button className='btn-app-secondary flex-fill' onClick={() => navigate('/')}>Przejdź na stronę główną</button>
        </div>
    )
}

export default OrderCompleted